// Figma: "Loyalty Cloud" file, node 18093:1036 ("Security") — a "Connected Accounts" card
// listing the log-in methods linked to the account: Google (with the account email and a
// Disconnect → Confirm / Cancel flow) and Email link (always available), plus a footer note.
// UI only: there's no API for unlinking Google yet, so Confirm just closes the prompt.
import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import {Box, ButtonBase, Stack, Typography} from '@mui/material';

import {tokens} from '../../themes/ThemeCustomization';
import {getEmail} from '../../app/store/reducers/session';
import AuthToast from '../authentication/components/AuthToast';
import googleIcon from '../../assets/icons/settings/google-icon.svg';
import spinnerIcon from '../../assets/icons/settings/spinner-icon.svg';
import emailLinkIcon from '../../assets/icons/settings/email-link-icon.svg';

const outlineButtonSx = {
    height: 32,
    px: 1.5,
    borderRadius: '6px',
    border: `1px solid ${tokens.color.neutral200}`,
    backgroundColor: tokens.color.white
};

const ConnectedBadge = () => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            height: 20,
            px: 1,
            borderRadius: '999px',
            border: `1px solid ${tokens.color.success200}`,
            backgroundColor: '#d9fbe8',
            color: tokens.color.success700,
            fontSize: '10px',
            fontWeight: 500,
            lineHeight: '16px'
        }}
    >
        Connected
    </Box>
);

const ConnectionItem = ({icon, iconSize = 20, name, description, actions}) => (
    <Stack direction="row" alignItems="center" spacing={2} sx={{py: 2}}>
        <Box
            sx={{
                width: 42,
                height: 42,
                flexShrink: 0,
                border: `1px solid ${tokens.color.neutral200}`,
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <Box component="img" src={icon} alt="" sx={{width: iconSize, height: iconSize}} />
        </Box>
        <Stack spacing="2px" sx={{flex: 1, minWidth: 0}}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.neutral950}}>
                    {name}
                </Typography>
                <ConnectedBadge />
            </Stack>
            <Typography variant="body2" noWrap sx={{color: tokens.color.neutral600}}>
                {description}
            </Typography>
        </Stack>
        {actions}
    </Stack>
);

const SecuritySettingsPage = () => {
    const email = useSelector(getEmail);
    const [confirming, setConfirming] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const [toast, setToast] = useState(null);

    // Loader state from Figma node 18093:1217: Confirm dims to 60% and shows a spinner while
    // the request runs. There's no unlink API yet — replace the await below when there is.
    const handleConfirm = async () => {
        setDisconnecting(true);
        try {
            await Promise.resolve();
            setToast({title: 'Google account has been disconnected successfully.', variant: 'success'});
        } catch {
            setToast({title: 'Unable to disconnect', description: "We couldn't disconnect your Google account. Please try again.", variant: 'error'});
        } finally {
            setDisconnecting(false);
            setConfirming(false);
        }
    };

    return (
        <>
            <AuthToast toast={toast} onClose={() => setToast(null)} />
        <Box sx={{backgroundColor: tokens.color.white, border: `1px solid ${tokens.color.neutral200}`, borderRadius: '8px', overflow: 'hidden'}}>
            <Box sx={{p: '20px', pb: 1}}>
                <Typography sx={{fontSize: '20px', fontWeight: 600, lineHeight: '28px', color: tokens.color.neutral950}}>Connected Accounts</Typography>
                <Typography variant="body2" sx={{color: tokens.color.neutral600, pt: 1.5, pb: 1}}>
                    Manage the log-in methods linked to your account.
                </Typography>

                <Box>
                    <ConnectionItem
                        icon={googleIcon}
                        name="Google"
                        description={email}
                        actions={
                            confirming ? (
                                <Stack direction="row" spacing={1.5}>
                                    <ButtonBase
                                        onClick={handleConfirm}
                                        disabled={disconnecting}
                                        aria-busy={disconnecting}
                                        sx={{
                                            height: 32,
                                            px: 1.5,
                                            gap: disconnecting ? 0.5 : 0,
                                            borderRadius: '6px',
                                            backgroundColor: tokens.color.error500,
                                            color: tokens.color.white,
                                            opacity: disconnecting ? 0.6 : 1,
                                            '&:hover': {opacity: disconnecting ? 0.6 : 0.9}
                                        }}
                                    >
                                        {disconnecting && (
                                            <Box
                                                component="img"
                                                src={spinnerIcon}
                                                alt=""
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    animation: 'security-spin 1s linear infinite',
                                                    '@keyframes security-spin': {from: {transform: 'rotate(0deg)'}, to: {transform: 'rotate(360deg)'}}
                                                }}
                                            />
                                        )}
                                        <Typography variant="body2" fontWeight={500} sx={{color: 'inherit'}}>
                                            Confirm
                                        </Typography>
                                    </ButtonBase>
                                    <ButtonBase onClick={() => setConfirming(false)} disabled={disconnecting} sx={outlineButtonSx}>
                                        <Typography variant="body2" fontWeight={500}>
                                            Cancel
                                        </Typography>
                                    </ButtonBase>
                                </Stack>
                            ) : (
                                <ButtonBase onClick={() => setConfirming(true)} sx={outlineButtonSx}>
                                    <Typography variant="body2" fontWeight={500}>
                                        Disconnect
                                    </Typography>
                                </ButtonBase>
                            )
                        }
                    />
                    <Box sx={{borderTop: `1px solid ${tokens.color.neutral200}`}}>
                        <ConnectionItem
                            icon={emailLinkIcon}
                            name="Email link"
                            description="Log in with a link sent to your email. Always available."
                        />
                    </Box>
                </Box>
            </Box>

            <Box sx={{backgroundColor: tokens.color.neutral50, borderTop: `1px solid ${tokens.color.neutral200}`, px: '20px', py: 2}}>
                <Typography variant="body2" sx={{color: tokens.color.neutral600}}>
                    Manage your connected accounts to control how you log in.
                </Typography>
            </Box>
        </Box>
        </>
    );
};

export default SecuritySettingsPage;
